import { ProfileForm } from './ProfileForm.js';
import { RaceList } from './RaceList.js';
import { RaceForm } from './RaceForm.js';

/**
 * EditorPanel — the main left-side container that orchestrates all editor sub-components.
 *
 * Composes ProfileForm, RaceList, and RaceForm (modal).
 * All user actions bubble up through the callbacks provided in the constructor.
 */
export class EditorPanel {
  /**
   * @param {Object} opts
   * @param {Function} opts.onProfileChange        - { name, subtitle, profileImageFile }
   * @param {Function} opts.onRaceAdd              - race data object
   * @param {Function} opts.onRaceEdit             - race data object (with id)
   * @param {Function} opts.onRaceDelete           - race id
   * @param {Function} opts.onRaceHighlightToggle  - race id
   * @param {Function} opts.onTemplateChange       - templateId string
   */
  constructor({ onProfileChange, onRaceAdd, onRaceEdit, onRaceDelete, onRaceHighlightToggle, onTemplateChange }) {
    this._onProfileChange = onProfileChange;
    this._onRaceAdd = onRaceAdd;
    this._onRaceEdit = onRaceEdit;
    this._onRaceDelete = onRaceDelete;
    this._onRaceHighlightToggle = onRaceHighlightToggle;
    this._onTemplateChange = onTemplateChange;

    this._container = null;
    this._currentResume = null;

    // Sub-components
    this._profileForm = new ProfileForm({
      onChange: (data) => this._onProfileChange?.(data),
    });

    this._raceList = new RaceList({
      onEdit: (race) => this._raceForm.show(race),
      onDelete: (id) => this._onRaceDelete?.(id),
      onHighlightToggle: (id) => this._onRaceHighlightToggle?.(id),
    });

    this._raceForm = new RaceForm({
      onSubmit: (data) => this._handleRaceSubmit(data),
      onCancel: () => {},
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /** Render the full editor panel into the given container. */
  render(container) {
    this._container = container;

    const panel = document.createElement('div');
    panel.className = 'editor-panel';

    // Heading
    const headingWrap = document.createElement('div');

    const heading = document.createElement('h2');
    heading.className = 'editor-heading';
    heading.textContent = 'Your Running Resume';
    headingWrap.appendChild(heading);

    const subtitle = document.createElement('p');
    subtitle.className = 'editor-subtitle';
    subtitle.textContent = 'Build and share your running profile';
    headingWrap.appendChild(subtitle);

    panel.appendChild(headingWrap);

    // Profile section
    const profileSection = document.createElement('div');
    this._profileForm.render(profileSection);
    panel.appendChild(profileSection);

    // Race History section
    const raceSection = document.createElement('div');
    this._raceList.render(raceSection);
    this._raceList.addOnAddClick(() => this._raceForm.show(null));
    panel.appendChild(raceSection);

    container.appendChild(panel);
  }

  /** Populate all sub-components with resume data. */
  setResume(resume) {
    if (!resume) return;

    this._currentResume = resume;

    this._profileForm.setData({
      name: resume.name,
      subtitle: resume.subtitle,
      profileImageUrl: resume.profileImageUrl,
    });

    this._raceList.setRaces(resume.races || []);
  }

  /** Get the current races from the race list. */
  getRaces() {
    return this._raceList._races;
  }

  /* ------------------------------------------------------------------ */
  /*  Internal                                                           */
  /* ------------------------------------------------------------------ */

  _handleRaceSubmit(data) {
    // If the data has an id, it's an edit; otherwise, it's a new race
    if (data.id != null) {
      this._onRaceEdit?.(data);
    } else {
      this._onRaceAdd?.(data);
    }
  }

}
